import { Button } from "@/components/generic/Button";
import { Input } from "@/components/generic/Field";
import { Heading } from "@/components/generic/Heading";
import { Select, SelectItem } from "@/components/generic/Select";
import { useIntegrationSetup } from "@/components/integrations/IntegrationSetupContext";
import { OAuthButton } from "@/components/integrations/OAuthButton";
import { Loader } from "@/components/layout/Loader";
import {
  useAvailableIntegrationsQuery,
  useIntegrationConfigurationMetadata,
  useIntegrationConnectionInfoMutation,
} from "@/queries/integrations";
import { joinRoom, usePushMessageListener } from "@/services/socket.service";
import {
  ProviderAuthenticationType,
  ProviderAuthenticationTypeLabels,
  ProviderConfigurationMetadata,
  ProviderConnectionInfo,
} from "@/types/integrations";
import { testIntegrationConnection } from "@/utils/integration-helpers";
import { Check, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

interface AuthenticationFormData {
  selectedConfigIndex?: number;
  [key: string]: string | boolean | number | undefined;
}

export interface AuthenticationStepProps {
  /** Called when the step completes and should proceed to next */
  onNext?: (updatedConnectionInfo?: ProviderConnectionInfo) => void;
  /** Called when user cancels */
  onCancel?: () => void;
  /** User object with organization ID for OAuth channel */
  user?: { lassoOrganizationId: string } | null;
}

export function AuthenticationStep({
  onNext,
  onCancel,
  user,
}: AuthenticationStepProps) {
  const {
    integration,
    mode,
    capabilities,
    connectionInfo,
    setConnectionInfo,
    oauthResponseChannelId,
    getSessionKey,
  } = useIntegrationSetup();

  const { data: configMetadata = [] } =
    useIntegrationConfigurationMetadata(integration);
  const { data: availableIntegrations = [] } = useAvailableIntegrationsQuery();
  const [pendingStepChange, setPendingStepChange] = useState(false);

  const { mutateAsync, isPending } = useIntegrationConnectionInfoMutation(
    integration,
    mode === "update" ? "put" : "post",
  );

  const currentIntegration = useMemo(
    () =>
      availableIntegrations?.find(
        (i) => i.uniqueName.toLowerCase() === integration,
      ) ?? null,
    [integration, availableIntegrations],
  );

  const defaultAuthValues = useMemo(() => {
    if (!connectionInfo) return {};
    switch (connectionInfo.authType) {
      case ProviderAuthenticationType.OAuthAndURL:
        return {
          oauthUrl: connectionInfo.apiUrl,
        };
      default:
        return {};
    }
  }, [connectionInfo]);

  const methods = useForm<AuthenticationFormData>({
    mode: "onChange",
    defaultValues: {
      selectedConfigIndex: 0,
      ...(mode === "update"
        ? defaultAuthValues
        : (JSON.parse(
            sessionStorage.getItem(getSessionKey("auth")) ?? "null",
          ) ?? {})),
    },
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { isValid, errors },
  } = methods;

  const selectedConfigIndex = watch("selectedConfigIndex");

  const [verifiedConnection, setVerifiedConnection] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);

  const oauthUrlField = watch("oauthUrl");
  const oauthUrl = useMemo(() => {
    if (
      typeof oauthUrlField === "string" &&
      oauthUrlField &&
      oauthUrlField.trim() !== ""
    ) {
      return oauthUrlField;
    }
    return undefined;
  }, [oauthUrlField]);

  const providerConfig = useMemo(() => {
    if (configMetadata.length === 0) return null;
    if (selectedConfigIndex !== undefined) {
      return configMetadata[selectedConfigIndex];
    }
    return configMetadata[0];
  }, [configMetadata, selectedConfigIndex]);

  const handleTestConnection = async (data: AuthenticationFormData) => {
    if (!currentIntegration || !isValid) return;
    setVerifiedConnection(false);
    setCheckingConnection(true);

    try {
      const { selectedConfigIndex, ...rest } = data;

      const success = await testIntegrationConnection(currentIntegration, {
        ...connectionInfo,
        ...rest,
      } as unknown as ProviderConfigurationMetadata);
      setVerifiedConnection(success);
    } catch (error) {
      console.error("Connection test failed:", error);
      setVerifiedConnection(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    if (checkingConnection) return;
    if (
      currentIntegration &&
      connectionInfo &&
      [
        ProviderAuthenticationType.OAuth,
        ProviderAuthenticationType.OAuthAndURL,
      ].includes(
        providerConfig?.authenticationType as ProviderAuthenticationType,
      )
    ) {
      testIntegrationConnection(currentIntegration, connectionInfo, true).then(
        (success) => {
          setVerifiedConnection(success);
        },
      );
    }
  }, [currentIntegration, connectionInfo, checkingConnection, providerConfig]);

  useEffect(() => {
    if (!user || !oauthResponseChannelId) return;
    const leave = joinRoom(
      `${user.lassoOrganizationId}_${oauthResponseChannelId}`,
    );
    return () => {
      leave();
    };
  }, [user, oauthResponseChannelId]);

  usePushMessageListener("OAuthCallback", (message) => {
    setConnectionInfo(message.message as ProviderConnectionInfo);
  });

  if (!configMetadata || configMetadata.length === 0) {
    return (
      <div className="py-8 text-center">
        <p>Kunne ikke indlæse konfiguration for denne integration.</p>
      </div>
    );
  }

  const onSubmit = async (data: AuthenticationFormData) => {
    if (capabilities?.storage) {
      const { selectedConfigIndex, ...rest } = data;
      await mutateAsync({
        data: {
          ...connectionInfo,
          ...rest,
        },
      });
      onNext?.();
      setConnectionInfo(null);
      return;
    }
    if (mode === "update") {
      const { selectedConfigIndex, ...rest } = data;
      const updatedConnectionInfo = {
        ...connectionInfo,
        ...rest,
      } as ProviderConnectionInfo;
      setConnectionInfo(updatedConnectionInfo);
      onNext?.(updatedConnectionInfo);
      return;
    }
    // Store form data in session storage
    sessionStorage.setItem(getSessionKey("auth"), JSON.stringify(data));

    if (!verifiedConnection) await handleTestConnection(data);

    if (verifiedConnection) {
      if (
        providerConfig?.authenticationType !==
          ProviderAuthenticationType.OAuth &&
        providerConfig?.authenticationType !==
          ProviderAuthenticationType.OAuthAndURL
      ) {
        const { selectedConfigIndex, ...rest } = data;

        setConnectionInfo({
          provider: integration,
          ...rest,
        } as ProviderConnectionInfo);
      }

      // Navigate to next step
      setPendingStepChange(true);
      onNext?.();
      setPendingStepChange(false);
    }
  };

  const getAuthenticationFields = (config: ProviderConfigurationMetadata) => {
    const fields: {
      name: string;
      type: "text" | "password" | "url" | "textarea" | "button" | "oauthButton";
      label: string;
      required?: boolean;
      disabled?: boolean;
      description?: string;
      value?: string;
      handle?: () => void;
    }[] = [];

    switch (config.authenticationType) {
      case ProviderAuthenticationType.OAuth:
        fields.push({
          name: "oauthButton",
          type: "oauthButton",
          label: `Forbind med ${currentIntegration?.name}`,
          required: true,
          description: `${currentIntegration?.name} åbner i et nyt vindue`,
        });
        break;
      case ProviderAuthenticationType.OAuthAndURL:
        fields.push({
          name: "oauthUrl",
          type: "text",
          label:
            integration === "dynamicscrm"
              ? "https://mitmiljø.crm4.dynamics.com"
              : config.apiUrlTitle!,
          required: true,
          description:
            integration === "dynamicscrm"
              ? "Dynamics 365 URL"
              : config.apiUrlDescription!,
          value: connectionInfo?.apiUrl,
        });
        fields.push({
          name: "oauthButton",
          type: "oauthButton",
          label: `Forbind med ${currentIntegration?.name}`,
          required: true,
          description: `${currentIntegration?.name} åbner i et nyt vindue`,
        });
        break;
      case ProviderAuthenticationType.OAuthClientCredentialsAndURL:
        fields.push({
          name: "oauthClientSecret",
          type: "text",
          label: config.accessKeyTitle!,
          required: true,
          description: config.accessKeyDescription || undefined,
          value: connectionInfo?.accessKey,
        });
        fields.push({
          name: "oauthUsername",
          type: "text",
          label: config.usernameTitle!,
          required: true,
          description: config.usernameDescription || undefined,
          value: connectionInfo?.userName,
        });
        fields.push({
          name: "oauthApiUrl",
          type: "text",
          label:
            integration === "dynamicscrm"
              ? "https://mitmiljø.crm4.dynamics.com"
              : config.apiUrlTitle!,
          required: true,
          description:
            integration === "dynamicscrm"
              ? "Dynamics 365 URL"
              : config.apiUrlDescription!,
          value: connectionInfo?.apiUrl,
        });
        break;
      case ProviderAuthenticationType.AccessKey:
        fields.push({
          name: "accessKey",
          type: "text",
          label: config.accessKeyTitle!,
          required: true,
          description: config.accessKeyDescription || undefined,
          value: connectionInfo?.accessKey,
        });
        break;
      case ProviderAuthenticationType.UsernameAndPassword:
        fields.push({
          name: "userName",
          type: "text",
          label: config.usernameTitle!,
          required: true,
          description: config.usernameDescription || undefined,
          value: connectionInfo?.userName,
        });
        fields.push({
          name: "password",
          type: "password",
          label: config.passwordTitle!,
          required: true,
          description: config.passwordDescription || undefined,
          value: connectionInfo?.password,
        });
        break;
      case ProviderAuthenticationType.UsernameAndPasswordAndURL:
        fields.push({
          name: "userName",
          type: "text",
          label: config.usernameTitle!,
          required: true,
          description: config.usernameDescription || undefined,
          value: connectionInfo?.userName,
        });
        fields.push({
          name: "password",
          type: "password",
          label: config.passwordTitle!,
          required: true,
          description: config.passwordDescription || undefined,
          value: connectionInfo?.password,
        });
        fields.push({
          name: "apiUrl",
          type: "url",
          label: config.apiUrlTitle!,
          required: true,
          description: config.apiUrlDescription || undefined,
          value: connectionInfo?.apiUrl,
        });
        break;
      default:
        break;
    }

    return fields;
  };

  const renderField = (
    field: ReturnType<typeof getAuthenticationFields>[number],
  ) => {
    const { name, type, label, required, description } = field;

    return (
      <div key={name} className="space-y-2 text-center">
        {description && type !== "oauthButton" && (
          <p className="text-theme-on-background-variant mb-2 text-left text-sm font-medium">
            {description}
          </p>
        )}

        {type === "textarea" ? (
          <textarea
            {...register(name, {
              required: required ? `${label} er påkrævet` : false,
              value: field.value,
            })}
            id={name}
            rows={4}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder={`Indtast ${label.toLowerCase()}`}
          />
        ) : type === "button" ? (
          <Button intent="secondary" onPress={field.handle}>
            Forbind med {currentIntegration?.name}
          </Button>
        ) : type === "oauthButton" ? (
          <OAuthButton
            providerConfig={providerConfig!}
            url={oauthUrl}
            disabled={!isValid}
            oauthResponseChannelId={oauthResponseChannelId}
            integration={currentIntegration ?? undefined}
            onFinished={() => setCheckingConnection(false)}
            onSuccess={(normalizedUrl) => {
              if (normalizedUrl) {
                setValue("oauthUrl", normalizedUrl);
              }
            }}
            onOauth={() => {
              setCheckingConnection(true);
              setVerifiedConnection(false);
            }}
          >
            {checkingConnection && <Loader className="size-6 animate-spin" />}
            {verifiedConnection ? (
              <>
                <Check className="size-5 text-green-600 dark:text-green-400" />
                Forbundet
              </>
            ) : (
              `Forbind med ${currentIntegration?.name}`
            )}
          </OAuthButton>
        ) : (
          <Input
            {...register(name, {
              required: required ? `Dette felt er påkrævet` : false,
              disabled: field.disabled,
              value: field.value,
            })}
            name={name}
            type={type}
            id={name}
            autoComplete="off"
            data-1p-ignore
            data-bwignore
            placeholder={label}
          />
        )}

        {description && type === "oauthButton" && (
          <p className="text-theme-on-background-variant mt-4 text-xs">
            {description}
          </p>
        )}

        {errors[name] && (
          <p className="text-sm text-red-500">
            {errors[name]?.message as string}
          </p>
        )}
      </div>
    );
  };

  const authFields = getAuthenticationFields(providerConfig!);

  return (
    <div className="flex h-full w-full flex-col">
      {/* Form */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col gap-y-14"
      >
        <div className="mx-auto flex flex-col items-center justify-center gap-y-8 xl:w-lg">
          {/* Configuration selector - only show if multiple configs */}
          {configMetadata.length > 1 && (
            <div className="w-full">
              <Select
                label="Vælg konfigurationstype"
                selectedKey={selectedConfigIndex?.toString()}
                onSelectionChange={(key) =>
                  setValue("selectedConfigIndex", Number(key))
                }
              >
                {configMetadata.map((config, index) => (
                  <SelectItem key={index.toString()} id={index.toString()}>
                    {ProviderAuthenticationTypeLabels[
                      config.authenticationType
                    ] || "Ukendt konfiguration"}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          {configMetadata.length === 1 &&
            providerConfig?.authenticationType ===
              ProviderAuthenticationType.API && (
              <Heading level={1} className="text-2xl">
                Forbindelse "{providerConfig.authenticationType}" ikke
                understøttet
              </Heading>
            )}

          <div className="flex w-full flex-col gap-y-8">
            {authFields.map(renderField)}
          </div>
          {currentIntegration &&
            providerConfig &&
            ![
              ProviderAuthenticationType.OAuth,
              ProviderAuthenticationType.OAuthAndURL,
            ].includes(providerConfig.authenticationType) && (
              <div className="mt-8 flex items-center justify-between gap-x-4">
                <Button
                  intent={
                    checkingConnection || !isValid ? "secondary" : "primary"
                  }
                  isDisabled={checkingConnection || !isValid}
                  onPress={() => handleTestConnection(methods.getValues())}
                >
                  Test forbindelse
                </Button>
              </div>
            )}
        </div>

        {/* Navigation buttons */}
        <div className="mt-auto flex justify-center gap-x-4 justify-self-end">
          {mode === "setup" && onCancel && (
            <Button type="button" intent="tertiary" onPress={onCancel}>
              Annuller
            </Button>
          )}

          <Button
            type="submit"
            intent={mode === "update" ? "primary" : "secondary"}
            isDisabled={
              checkingConnection ||
              isPending ||
              pendingStepChange ||
              !isValid ||
              (!checkingConnection && !verifiedConnection)
            }
          >
            {(pendingStepChange || isPending) && (
              <LoaderCircle className="mr-2 size-4 shrink-0 animate-spin" />
            )}
            <span>
              {mode === "update"
                ? isPending
                  ? "Gemmer..."
                  : "Gem ændringer"
                : capabilities?.storage
                  ? "Gem og afslut"
                  : "Næste"}
            </span>
          </Button>
        </div>
      </form>
    </div>
  );
}
